/**
 * Parse the session number from a calendar event title.
 * Titles can be in the format "Client Name (X/Y)" where X is the session number and Y is the package size.
 * 
 * IMPORTANT: Calendar titles only get updated with session numbers the night of the session.
 * So we only use parsed values for PAST sessions. Future sessions use the client's upcoming number.
 * Completed sessions today (1 hour after start) use previous_session_number.
 * 
 * @param calendarEventTitle - The calendar event title to parse
 * @param fallbackSessionNumber - The session number to use if parsing fails (from database)
 * @param sessionDate - The date/time of the session (to determine if it's past, completed today, or future)
 * @param clientUpcomingSessionNumber - The client's upcoming_session_number for future sessions
 * @param clientPreviousSessionNumber - The client's previous_session_number for completed sessions today
 */
export function parseSessionNumber(
  calendarEventTitle: string | null | undefined,
  fallbackSessionNumber: number,
  sessionDate?: Date | string,
  clientUpcomingSessionNumber?: number,
  clientPreviousSessionNumber?: number
): number {
  // First, try to parse from the calendar title if it exists
  if (calendarEventTitle) {
    // Match pattern like "(5/20)" or "(29/30)"
    const match = calendarEventTitle.match(/\((\d+)\/(\d+)\)/);
    if (match) {
      const parsed = parseInt(match[1], 10);
      if (!isNaN(parsed)) return parsed;
    }
  }
  
  // If we have a session date, determine if session is completed, in-progress, or future
  if (sessionDate) {
    const sessionDateTime = typeof sessionDate === 'string' ? new Date(sessionDate) : sessionDate;
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrowStart = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000);
    
    // Session end time = start time + 1 hour (in-person sessions are 1 hour)
    const sessionEndTime = new Date(sessionDateTime.getTime() + 60 * 60 * 1000);
    
    // Check if session is TODAY
    const isToday = sessionDateTime >= todayStart && sessionDateTime < tomorrowStart;
    
    if (isToday) {
      // For TODAY's sessions:
      // - If session_number is set (non-zero), use it
      // - Otherwise, use the client's upcoming_session_number as fallback
      if (fallbackSessionNumber > 0) {
        return fallbackSessionNumber;
      }
      return clientUpcomingSessionNumber || fallbackSessionNumber;
    }
    
    // For PAST sessions (before today), use the fallback (parsed from calendar title or database session_number)
    // We don't use previous_session_number here because that's the client's CURRENT state, not the state at time of session
    if (sessionDateTime < todayStart) {
      return fallbackSessionNumber;
    }
    
    // For FUTURE sessions (after today), use upcoming_session_number
    if (sessionDateTime >= tomorrowStart && clientUpcomingSessionNumber) {
      return clientUpcomingSessionNumber;
    }
  }
  
  return fallbackSessionNumber;
}

/**
 * Parse the package size from a calendar event title.
 * Titles can be in the format "Client Name (X/Y)" where X is the session number and Y is the package size.
 * 
 * @param calendarEventTitle - The calendar event title to parse
 * @param fallbackPackageSize - The package size to use if parsing fails
 * @param sessionDate - The date of the session (to determine if it's past or future)
 */
export function parsePackageSize(
  calendarEventTitle: string | null | undefined,
  fallbackPackageSize: number,
  sessionDate?: Date | string
): number {
  // First try to parse from title
  if (calendarEventTitle) {
    // Match pattern like "(5/20)" or "(29/30)"
    const match = calendarEventTitle.match(/\((\d+)\/(\d+)\)/);
    if (match) {
      const parsed = parseInt(match[2], 10);
      if (!isNaN(parsed)) return parsed;
    }
  }
  
  return fallbackPackageSize;
}